/**
 * Floating labels over the canvas (ADR 0005: text is DOM, never drawn in
 * WebGL): damage and heal numbers, exp, level-ups, skills learned, status
 * names, stage chevrons, misses, the names of skills, items and passives, and
 * items lost. Each rises and fades over its kind's duration (`uiTiming`). The
 * shell creates them as the renderer reaches each event, at the screen
 * position the renderer projects; the text comes from the string table
 * (`ui.label.*` keys and content names). A label for something the player
 * cannot see is kept (and counted by the shell) but not shown.
 */
import type { CSSProperties } from 'react';
import type { Strings } from '@ab/session';
import { type TextArgs, formatText } from './text.ts';
import type { LabelKind } from './timing.ts';
import './overlays.css';

export type LabelRequest = TextArgs & {
  /** Unique while the label lives. */
  readonly id: number;
  readonly kind: LabelKind;
  readonly key: string;
  /** Screen position in CSS pixels; null centres it (nothing to anchor to). */
  readonly at: { readonly x: number; readonly y: number } | null;
  readonly visible: boolean;
  readonly durationMs: number;
};

export type FloatingLabelsProps = { readonly labels: readonly LabelRequest[]; readonly strings: Strings };

export const FloatingLabels = ({ labels, strings }: FloatingLabelsProps) => (
  <div className="ab-labels" data-testid="floating-labels">
    {labels
      .filter((l) => l.visible)
      .map((l) => {
        const style: CSSProperties = { animationDuration: `${l.durationMs}ms`, ...(l.at === null ? { left: '50%', top: '40%' } : { left: `${l.at.x}px`, top: `${l.at.y}px` }) };
        return (
          <div key={l.id} className={`ab-label ab-label-${l.kind}`} style={style} data-kind={l.kind} data-id={l.id}>
            {formatText(strings, l.key, l)}
          </div>
        );
      })}
  </div>
);
